/**
 * What we actually KNOW about a dancer at a class meeting, and nothing more.
 *
 * A missing attendance row is not an absence. It is a hole in the record, and
 * every count built on top of attendance in this app now has to say which of
 * the two it is looking at. Three separate features used to blur that line in
 * three different ways, so this file is the one place the distinction is
 * defined and the one place it is tested.
 *
 * THE MODEL, in full. For one dancer at one meeting of one class:
 *
 *   'attended'    an explicit present or late row. They walked in.
 *   'missed'      an explicit absent row, plus excused where the caller counts
 *                 it (isMiss below is the caller's, on purpose: the dance
 *                 rules count excused, staff reliability does not).
 *   'unmarked'    the class registered SOMEBODY that day and this dancer has
 *                 no row. The class met. We still do not know about them.
 *   'unrecorded'  nobody registered anything that day. Nothing is known about
 *                 the evening at all.
 *
 * And what each one does to a run of misses:
 *
 *   'missed'      extends the run.
 *   'attended'    ends it.
 *   'unrecorded'  SKIPPED. Neither extends nor ends. A teacher who forgets to
 *                 open attendance must not be able to demote anybody, and must
 *                 not be able to un-demote anybody either.
 *   'unmarked'    a HOLE. It never counts as a miss and it never ends a run
 *                 that has already happened, but a later miss starts counting
 *                 again from one, because "three in a row" cannot be claimed
 *                 across a meeting nobody answered for. Trailing holes are
 *                 ignored for the same reason 'unrecorded' is: this week's
 *                 paperwork does not rewrite last month's facts.
 *
 * WHY THE MEETING SEQUENCE IS CALENDAR-AWARE. The obvious shortcut, "a gap of
 * more than a week breaks the run", is wrong twice over. An A/B week class
 * meets every FOURTEEN days, so a blanket seven day rule makes three in a row
 * mathematically impossible for it. And spring break puts a two week hole in
 * every weekly class in the studio, so the same rule would quietly erase a
 * real absence streak the week the studio reopened. A date is only a meeting
 * if the class's own cadence says it met and the studio was open, which is why
 * expandMeetingDates below takes the cadence and the closures rather than
 * counting days.
 *
 * This file is arithmetic only. It imports NOTHING so `node
 * --experimental-strip-types` can run it directly in
 * tests/attendance-integrity.test.mjs, the same discipline lib/alternates.ts
 * and lib/tonight.ts already follow. Modules that DO import it must spell the
 * path with the .ts extension (hub/tsconfig.json sets
 * allowImportingTsExtensions for exactly this reason): node's loader will not
 * resolve an extensionless relative import, and both spellings were proven by
 * running them, not by reading about them.
 */

/** The four ways a meeting can be marked. Mirrors AttendanceStatus in lib/attendance.ts. */
export type AttendanceMarkStatus = 'present' | 'absent' | 'late' | 'excused';

/** One attendance row, reduced to what the integrity maths needs. */
export interface AttendanceIntegrityMark {
  /** 'YYYY-MM-DD'. Compared as a string, so the format is load bearing. */
  date: string;
  status: AttendanceMarkStatus;
}

/** A studio closure, in the shape lib/closures.ts stores it. */
export interface ClosureRange {
  /** Inclusive, 'YYYY-MM-DD'. */
  startDate: string;
  /** Inclusive, 'YYYY-MM-DD'. Equals startDate for a single day. */
  endDate: string;
}

/** See the file header. This is the whole vocabulary. */
export type MeetingKnowledge = 'attended' | 'missed' | 'unmarked' | 'unrecorded';

export interface MeetingObservation {
  /** 'YYYY-MM-DD' of the meeting. */
  date: string;
  knowledge: MeetingKnowledge;
  /** The explicit status behind 'attended' or 'missed'. Null for both unknowns. */
  status: AttendanceMarkStatus | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A ceiling on how many days expandMeetingDates will walk, so a malformed
 * term (an end date in 2099, a swapped pair) costs one wasted loop rather than
 * a hung tab. Comfortably more than the longest window any caller uses: the
 * alternates lookback is 120 days and the staff report is 90.
 */
export const MAX_MEETING_SPAN_DAYS = 800;

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE.test(value);
}

export function isKnownStatus(value: unknown): value is AttendanceMarkStatus {
  return value === 'present' || value === 'absent' || value === 'late' || value === 'excused';
}

/**
 * Add (or subtract, with a negative) whole days to an ISO date.
 *
 * UTC arithmetic on purpose. Attendance dates are written in the studio's
 * local time, but this function only ever moves between two all-day keys, and
 * doing that in local time makes the answer depend on whichever daylight
 * saving boundary the caller happens to straddle. UTC has none.
 */
export function addIsoDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, (m ?? 1) - 1, (d ?? 1) + days));
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Monday of the ISO week containing `dateKey`, as 'YYYY-MM-DD'. */
export function mondayIso(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  const weekday = date.getUTCDay();
  return addIsoDays(dateKey, weekday === 0 ? -6 : 1 - weekday);
}

/** True when the studio was shut on this date. ISO strings sort chronologically. */
export function isClosedOn(
  closures: readonly ClosureRange[] | undefined,
  dateKey: string
): boolean {
  if (!closures) return false;
  for (const c of closures) {
    if (!c || !isIsoDate(c.startDate) || !isIsoDate(c.endDate)) continue;
    if (c.startDate <= dateKey && dateKey <= c.endDate) return true;
  }
  return false;
}

export interface MeetingDatesInput {
  /** Inclusive ISO start of the span to walk. */
  from: string;
  /** Inclusive ISO end. Callers bound this at today: a class that has not happened yet cannot be missed. */
  to: string;
  /**
   * True when this class's own cadence puts a meeting on this date, closures
   * aside. Real callers pass classSeriesRunsOnDate from lib/classSeries.ts so
   * the A/B week rules live in exactly one place in the app and this file
   * never has a second, drifting copy of them. Tests pass their own.
   */
  meetsOn: (dateKey: string) => boolean;
  closures?: readonly ClosureRange[];
  /**
   * Dates this class registered anybody at all. Always a meeting whatever the
   * cadence says: a register is proof the class happened, which is how a
   * make-up, a moved rehearsal, or a class held during a listed closure still
   * counts.
   */
  registerDates?: Iterable<string>;
}

/**
 * Every date this class actually met inside a window, oldest first.
 *
 * Cadence minus closures, plus every date with a register. An invalid or
 * inverted span falls back to the register dates alone, which is the honest
 * answer when there is no cadence to expand: we still know about the days
 * somebody wrote something down.
 */
export function expandMeetingDates(input: MeetingDatesInput): string[] {
  const dates = new Set<string>();
  for (const date of input.registerDates ?? []) {
    if (isIsoDate(date)) dates.add(date);
  }

  if (isIsoDate(input.from) && isIsoDate(input.to) && input.from <= input.to) {
    let cursor = input.from;
    for (let i = 0; i < MAX_MEETING_SPAN_DAYS && cursor <= input.to; i += 1) {
      if (!isClosedOn(input.closures, cursor) && input.meetsOn(cursor)) dates.add(cursor);
      cursor = addIsoDays(cursor, 1);
    }
  }

  return [...dates].sort();
}

export interface ObserveMeetingsInput {
  /** This dancer's rows. One per date wins, the last one listed. */
  marks: readonly AttendanceIntegrityMark[];
  /**
   * Every date the class met, from expandMeetingDates. Omit and only registers
   * and this dancer's own rows are meetings.
   *
   * Worth being precise about what this buys, because it is easy to expect the
   * wrong thing from it: a cadence meeting with no register is 'unrecorded',
   * and missRun skips 'unrecorded' entirely, so supplying the cadence can never
   * on its own change a run. That is the POINT. The cadence and the closure
   * list are how we know a fourteen day gap in an A/B class and a two week
   * spring break are not evidence of anything, which is exactly what a
   * gap-counting rule gets wrong. What the cadence does change is the RECORD:
   * observations carry 'unrecorded' for a meeting nobody wrote down, so a
   * caller that wants to say "attendance was not taken twice in this window"
   * can, instead of having to infer it from silence.
   */
  meetingDates?: Iterable<string>;
  /** Every date the class registered anybody, so an unmarked dancer reads as a hole rather than vanishing. */
  registerDates?: Iterable<string>;
  /**
   * Which explicit statuses count as a miss. The caller's call, and the two
   * callers deliberately disagree: isAlternateMiss in lib/alternates.ts counts
   * excused (the eight count still has a hole in it), while staff reliability
   * in lib/staffAttendanceMath.ts does not (telling us in advance is the right
   * thing to do). Neither should be changed to match the other.
   */
  isMiss: (status: AttendanceMarkStatus) => boolean;
}

/**
 * What we know at every meeting, oldest first.
 *
 * A dated mark is ALWAYS a meeting, cadence or not. Somebody wrote it down, so
 * the class happened. That is also what makes this backwards compatible: hand
 * it marks and nothing else and every observation is 'attended' or 'missed',
 * exactly the sequence the old marks-only arithmetic saw.
 */
export function observeMeetings(input: ObserveMeetingsInput): MeetingObservation[] {
  const byDate = new Map<string, AttendanceMarkStatus>();
  for (const mark of input.marks) {
    if (!mark || !isIsoDate(mark.date) || !isKnownStatus(mark.status)) continue;
    byDate.set(mark.date, mark.status);
  }

  const registers = new Set<string>();
  for (const date of input.registerDates ?? []) {
    if (isIsoDate(date)) registers.add(date);
  }

  // A register is a meeting whether or not the cadence agrees, and so is this
  // dancer's own row. Without the registers in here a registered day with no
  // row for THIS dancer would produce no observation at all, and the hole the
  // whole model turns on would silently never appear.
  const meetings = new Set<string>(byDate.keys());
  for (const date of registers) meetings.add(date);
  for (const date of input.meetingDates ?? []) {
    if (isIsoDate(date)) meetings.add(date);
  }

  return [...meetings].sort().map((date) => {
    const status = byDate.get(date);
    if (status) {
      return {
        date,
        knowledge: input.isMiss(status) ? ('missed' as const) : ('attended' as const),
        status,
      };
    }
    return {
      date,
      knowledge: registers.has(date) ? ('unmarked' as const) : ('unrecorded' as const),
      status: null,
    };
  });
}

export interface MissRun {
  /** Misses in a row ending at the most recent meeting we have an answer for. */
  current: number;
  /** The exact dates behind that count, oldest first. Somebody's evidence, so it travels with the number. */
  currentDates: string[];
  /** The longest such run anywhere in the sequence. */
  longest: number;
}

/**
 * The run of misses, counted honestly. See the file header for what each kind
 * of knowledge does; the only subtle part is the hole.
 *
 * A hole ('unmarked') sets a flag rather than resetting on the spot. That way
 * a hole AFTER a completed run leaves the run alone (three real absences are
 * still three real absences whatever this week's paperwork looks like), while
 * a hole BETWEEN two misses makes the later one start over, because nobody can
 * honestly say "in a row" across a meeting that was never answered for.
 */
export function missRun(observations: readonly MeetingObservation[]): MissRun {
  let current = 0;
  let currentDates: string[] = [];
  let longest = 0;
  let holeSinceLastMiss = false;

  for (const observation of observations) {
    if (observation.knowledge === 'unrecorded') continue;
    if (observation.knowledge === 'unmarked') {
      holeSinceLastMiss = true;
      continue;
    }
    if (observation.knowledge === 'attended') {
      current = 0;
      currentDates = [];
      holeSinceLastMiss = false;
      continue;
    }
    if (holeSinceLastMiss) {
      current = 0;
      currentDates = [];
      holeSinceLastMiss = false;
    }
    current += 1;
    currentDates = [...currentDates, observation.date];
    if (current > longest) longest = current;
  }

  return { current, currentDates, longest };
}

/** Every explicit miss in the sequence, oldest first. Unknowns can never appear here. */
export function totalMisses(observations: readonly MeetingObservation[]): string[] {
  return observations.filter((o) => o.knowledge === 'missed').map((o) => o.date);
}

// ---------------------------------------------------------------------------
// The studio-wide weekly version: "we have not seen this dancer in N weeks".
//
// Different question, same model, one week coarser. Kim's flag is per DANCER
// across every class they are in, not per dance, so the unit here is a
// calendar week and a week's knowledge is the aggregate of that week's rows.
// ---------------------------------------------------------------------------

/** One attendance row as the studio-wide sweep reads it. */
export interface StudioAttendanceMark {
  offeringId: string;
  studentId: string;
  date: string;
  status: AttendanceMarkStatus;
}

export interface ConsecutiveWeekAbsenceFlag {
  studentId: string;
  /** Weeks in a row, ending at the most recent week we have an answer for, with an explicit absent or excused. */
  weeksMissed: number;
  /** Most recent date this dancer was marked present or late, or null if none on file. */
  lastPresentDate: string | null;
  /**
   * The classes this dancer was marked in inside the window, most recently
   * marked first. The Command Center's "Message the family" button uses it
   * to pick the class she is actually missing, and to notice when none of
   * those classes still lists her on its roster.
   */
  offeringIds: string[];
}

export interface ConsecutiveWeekAbsenceInput {
  marks: readonly StudioAttendanceMark[];
  /** The bar. Three by default at the call site. */
  weeks: number;
  /** How many weeks back to walk. Bounded by whatever window the caller queried. */
  queryWeeks: number;
  /** Today, 'YYYY-MM-DD', passed in so nothing here reads the clock. */
  todayDate: string;
}

interface StudentWeeks {
  offeringIds: Set<string>;
  /** offeringId -> the latest date this dancer has a row in that class. */
  lastMarkedByOffering: Map<string, string>;
  attendedWeeks: Set<string>;
  missedWeeks: Set<string>;
  earliestDate: string;
  lastPresentDate: string | null;
}

/**
 * Dancers who have gone `weeks` or more consecutive weeks with an explicit
 * absent or excused and nothing else, so Kim can call the family before it
 * turns into a bigger problem.
 *
 * The eligibility model is the part worth keeping from the hook this replaced:
 * a week only counts at all if one of THIS dancer's classes registered
 * somebody. A week where none of them took attendance says nothing about the
 * dancer and is skipped. What changed is what an eligible week with no row for
 * this dancer does. It used to count as a missed week, which is the exact
 * "missing row means absent" claim this file exists to stop: the teacher may
 * simply not have got to that name. It is a hole now, so it never adds to the
 * count, and a run that has to reach back across one starts over on the far
 * side of it.
 *
 * Closures need no special case here. A closed week has no rows in it from
 * anybody, so it is already 'unrecorded' and already skipped.
 *
 * Still true, and still deliberate: "the dancer's classes" is inferred from
 * the offerings on their own rows inside the window, not from the live roster,
 * and a dancer needs at least one row from before the run started so a brand
 * new student is never mistaken for one who has gone missing.
 */
export function consecutiveWeekAbsenceFlags(
  input: ConsecutiveWeekAbsenceInput
): ConsecutiveWeekAbsenceFlag[] {
  const byStudent = new Map<string, StudentWeeks>();
  /** Weeks a given offering registered anybody at all. The eligibility model. */
  const registeredWeeks = new Map<string, Set<string>>();

  for (const mark of input.marks) {
    if (!mark || !isIsoDate(mark.date) || !isKnownStatus(mark.status)) continue;
    if (!mark.studentId || !mark.offeringId) continue;
    const week = mondayIso(mark.date);

    let weeks = registeredWeeks.get(mark.offeringId);
    if (!weeks) {
      weeks = new Set();
      registeredWeeks.set(mark.offeringId, weeks);
    }
    weeks.add(week);

    let agg = byStudent.get(mark.studentId);
    if (!agg) {
      agg = {
        offeringIds: new Set(),
        lastMarkedByOffering: new Map(),
        attendedWeeks: new Set(),
        missedWeeks: new Set(),
        earliestDate: mark.date,
        lastPresentDate: null,
      };
      byStudent.set(mark.studentId, agg);
    }
    agg.offeringIds.add(mark.offeringId);
    const lastMarked = agg.lastMarkedByOffering.get(mark.offeringId);
    if (!lastMarked || mark.date > lastMarked) agg.lastMarkedByOffering.set(mark.offeringId, mark.date);
    if (mark.date < agg.earliestDate) agg.earliestDate = mark.date;
    if (mark.status === 'present' || mark.status === 'late') {
      agg.attendedWeeks.add(week);
      if (!agg.lastPresentDate || mark.date > agg.lastPresentDate) {
        agg.lastPresentDate = mark.date;
      }
    } else {
      agg.missedWeeks.add(week);
    }
  }

  const todayWeek = mondayIso(input.todayDate);
  const out: ConsecutiveWeekAbsenceFlag[] = [];

  for (const [studentId, agg] of byStudent) {
    let cursor = todayWeek;
    let missed = 0;
    let earliestMissedWeek: string | null = null;
    let runStarted = false;

    // Backwards from this week. Same rules as missRun, read in the other
    // direction: a hole before the run has started is just this week's
    // paperwork and is skipped, a hole inside the run ends the claim.
    for (let i = 0; i < input.queryWeeks; i += 1) {
      if (agg.attendedWeeks.has(cursor)) break;
      if (agg.missedWeeks.has(cursor)) {
        runStarted = true;
        missed += 1;
        earliestMissedWeek = cursor;
      } else {
        const registered = [...agg.offeringIds].some((offeringId) =>
          registeredWeeks.get(offeringId)?.has(cursor)
        );
        if (registered && runStarted) break;
      }
      cursor = addIsoDays(cursor, -7);
    }

    if (missed < input.weeks || !earliestMissedWeek) continue;
    if (!(agg.earliestDate < earliestMissedWeek)) continue;

    const offeringIds = [...agg.lastMarkedByOffering.entries()]
      .sort((a, b) => b[1].localeCompare(a[1]) || a[0].localeCompare(b[0]))
      .map(([offeringId]) => offeringId);
    out.push({ studentId, weeksMissed: missed, lastPresentDate: agg.lastPresentDate, offeringIds });
  }

  out.sort((a, b) => b.weeksMissed - a.weeksMissed || a.studentId.localeCompare(b.studentId));
  return out;
}
